import { useState, useEffect, useMemo } from "react"
import { ArrowDownOutlined } from "@ant-design/icons"
import { useLocation } from "react-router-dom"
import { Segmented, Dropdown, Row, Col, Input, Tooltip, Button, Spin } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { dealDetailedView } from "../../redux/slices/dealsSlice"
import Loader from "../../components/CustomLoader/Loader"
import DealDetails from "./Components/DealDetails"
import DealLevelFunding from "./Components/DealLevelFunding"
import DealLevelSettlement from "./Components/DealLevelSettlement"
import DealActivityLog from "./Components/DealActivityLog"
import BackButton from "../../components/CustomButton/BackButton"
import PrimaryButton from "../../components/CustomButton/PrimaryButton"
import { BTN_TXT, TITLE_TXT } from "../../constants/constants"
import useSearchableTable from "../../hooks/useSearchableTable"
import { DownloadOutlined, FilePdfOutlined } from "@ant-design/icons"
import { detailedViewDownloadPageHandle } from "../../helpers/helpers"
import { isSupplyChainCategory } from "../../helpers/dealCategoryProducts";

// Maps status IDs / names to keys used for tab visibility.
const STATUS_KEY_MAP = {
    DSC1000001: "pending",
    DSC1000002: "approved",
    DSC1000003: "expired",
    DSC1000004: "funded",
    DSC1000005: "funded",
    DSC1000006: "disbursed",
    DSC1000007: "cancelled",
    DSC1000008: "repayment",
    DSC1000009: "partiallySettled",
    DSC1000010: "settled",
    DSC1000011: "closed",
    DSC1000012: "rejected",
    DSC1000017: "pendingDisbursement",
};

const FUNDING_VISIBLE_KEYS = new Set([
    "funded",
    "pendingDisbursement",
    "disbursed",
    "repayment",
    "partiallySettled",
    "settled",
    "closed",
]);

// Settlement records exist only after repayment/settlement activity starts.
const SETTLEMENT_VISIBLE_KEYS = new Set([
    "repayment",
    "partiallySettled",
    "settled",
    "closed",
]);

const INVOICE_FINANCING_CATEGORY_ID = "DCT1000001";
const SUPPLY_CHAIN_FINANCING_CATEGORY_ID = "DCT1000006";

/** Activity Log tab: Invoice Financing and Supply Chain deals only. */
const supportsDealActivityLog = (summary = {}) => {
    const categoryId = String(summary?.dealCategoryId || "").trim();
    if (categoryId === INVOICE_FINANCING_CATEGORY_ID || categoryId === SUPPLY_CHAIN_FINANCING_CATEGORY_ID) return true;
    const dealType = String(summary?.dealType || summary?.dealCategoryName || "")
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
    return dealType.includes("invoicefinanc") || dealType.includes("supplychain");
};

const resolveDealStatusKey = (statusId, statusName) => {
    const rawStatusId = Array.isArray(statusId) ? (statusId.length === 1 ? statusId[0] : null) : statusId;
    if (rawStatusId && STATUS_KEY_MAP[rawStatusId]) {
        return STATUS_KEY_MAP[rawStatusId];
    }
    const normalized = String(statusName || "")
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
    if (!normalized) return null;
    if (normalized.includes("pendingdisburs")) return "pendingDisbursement";
    if (normalized.includes("partial")) return "partiallySettled";
    if (normalized === "disbursed") return "disbursed";
    if (normalized.includes("repay")) return "repayment";
    if (normalized === "settled") return "settled";
    if (normalized === "closed") return "closed";
    if (normalized === "funded" || normalized === "agreement") return "funded";
    if (normalized === "approved") return "approved";
    if (normalized === "pending") return "pending";
    if (normalized === "expired") return "expired";
    if (normalized === "cancelled") return "cancelled";
    if (normalized === "rejected") return "rejected";
    return normalized;
};

const DealDetailView = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { dealId, selectedStatus } = location?.state || {};
    const { detailedview, loading } = useSelector((state) => state?.deals);
    const { loading: docLoading } = useSelector((state) => state.documents);
    const [selectedTab, setSelectedTab] = useState('dealDetails');

    const {
        searchText = "",
        setSearchText = () => { },
        filteredData = [],
    } = useSearchableTable(detailedview?.dealLevelFunding) || {};

    useEffect(() => {
        dispatch(dealDetailedView(dealId));
    }, [dispatch, dealId])

    const statusKey = useMemo(
        () =>
            resolveDealStatusKey(
                selectedStatus,
                detailedview?.dealLevelSummary?.dealStatus
            ),
        [selectedStatus, detailedview?.dealLevelSummary?.dealStatus]
    );

    const showActivityLog = useMemo(
        () => supportsDealActivityLog(detailedview?.dealLevelSummary),
        [detailedview?.dealLevelSummary]
    );

    const dealSegments = useMemo(() => {
        const segments = [
            {
                label: TITLE_TXT.DEAL_DETS,
                value: "dealDetails",
            },
        ];
        if (FUNDING_VISIBLE_KEYS.has(statusKey)) {
            segments.push({
                label: TITLE_TXT.DEAL_LEVEL_FUND,
                value: "dealLevelFunding",
            });
        }
        if (SETTLEMENT_VISIBLE_KEYS.has(statusKey)) {
            segments.push({
                label: TITLE_TXT.DEAL_LEVEL_SETTLE,
                value: "dealLevelSettlement",
            });
        }
        if (showActivityLog) {
            segments.push({
                label: TITLE_TXT.ACTIVITY_LOG,
                value: "activityLog",
            });
        }
        return segments;
    }, [statusKey, showActivityLog]);

    useEffect(() => {
        const allowed = new Set(dealSegments.map((s) => s.value));
        if (!allowed.has(selectedTab)) {
            setSelectedTab("dealDetails");
        }
    }, [dealSegments, selectedTab]);

    const downloadInvoices = (fileUrl) => {
        if (!fileUrl) return;
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = "";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // 📥 API based blob download (detailedViewDownload)

    const handleDownload = (type) => detailedViewDownloadPageHandle({ dispatch, type: "dealPdf", id: dealId });

    const isSupplyChain = isSupplyChainCategory(
        detailedview?.dealLevelSummary?.dealCategoryId ||
        detailedview?.dealLevelSummary?.dealType ||
        location?.state?.dealCategoryId
    );

    const menuItems = [
        {
            key: "1",
            label: (
                <span onClick={() => downloadInvoices(detailedview?.dealLevelSummary?.downloadUrl?.downloadInvoiceSummary)} style={{ margin: 10 }}>
                    {isSupplyChain ? (BTN_TXT.DWNLD_PO_CSV || "Download Purchase Order CSV") : BTN_TXT.DWNLD_INV_CSV}
                </span>
            ),
        },
        { type: "divider" },
        {
            key: "2",
            label: (
                <span onClick={() => downloadInvoices(detailedview?.dealLevelSummary?.downloadUrl?.downloadInvoiceFile)} style={{ margin: 10 }}>
                    {isSupplyChain ? (BTN_TXT.DWNLD_PO_ZIP || "Download Purchase Order Zip") : BTN_TXT.DWNLD_INV_ZIP}
                </span>
            ),
        },
    ]

    // ⬇ Deal summary dropdown (API based)
    const downloadItems = [
        {
            key: "pdf",
            label: "Download as PDF",
            icon: <FilePdfOutlined />,
        },
    ];



    return (
        <div className="deal-details-container">
            <BackButton />
            <Row gutter={[0, 16]} align="middle" justify="space-between" style={{ width: "100%" }}>
                <Col>
                    <div className="segmented-wrapper">
                        <Segmented
                            options={dealSegments}
                            value={selectedTab}
                            onChange={(value) => setSelectedTab(value)}
                            className="custom-segmented"
                        />
                    </div>
                </Col>
                <Col>
                    {selectedTab === "dealDetails" ? (
                        <Row gutter={8} align="middle">
                            <Col>
                                <Dropdown
                                    menu={{ items: menuItems }}
                                    trigger={["click"]}
                                    overlayStyle={{
                                        border: "1px solid #d9d9d9",
                                        borderRadius: "8px",
                                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                                    }}
                                >
                                    <PrimaryButton
                                        variant="solid"
                                        color="primary"
                                        icon={<ArrowDownOutlined />}
                                        iconPosition="end"
                                    >
                                        {BTN_TXT.DOWNLOAD}
                                    </PrimaryButton>
                                </Dropdown>
                            </Col>
                            <Col>
                                <Dropdown
                                    menu={{ items: downloadItems, onClick: handleDownload }}
                                    disabled={docLoading}
                                >
                                    <Tooltip
                                        title={
                                            docLoading
                                                ? "Downloading..."
                                                : "Download"
                                        }
                                    >
                                        <Button
                                            type="primary"
                                            shape="circle"
                                            size="large"
                                            disabled={docLoading}
                                            icon={
                                                docLoading ? (
                                                    <Spin size="small" />
                                                ) : (
                                                    <DownloadOutlined />
                                                )
                                            }
                                        />
                                    </Tooltip>
                                </Dropdown>
                            </Col>
                        </Row>
                    ) : selectedTab === "activityLog" ? null : (
                        <Input
                            placeholder="Search"
                            allowClear
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="search-input"
                        />
                    )}
                </Col>
            </Row>
            {loading ? <Loader /> : (
                <>
                    {selectedTab === "dealDetails" && (
                        <DealDetails deal={detailedview?.dealLevelSummary} />
                    )}
                    {selectedTab === "dealLevelFunding" && (
                        <DealLevelFunding deal={filteredData} status={detailedview?.dealLevelSummary?.dealStatus} dealId={dealId} />
                    )}
                    {selectedTab === "dealLevelSettlement" && (
                        <DealLevelSettlement deal={detailedview?.dealLevelSettlement} status={selectedStatus} />
                    )}
                    {selectedTab === "activityLog" && showActivityLog && (
                        <DealActivityLog
                            dealId={dealId || detailedview?.dealLevelSummary?.dealId}
                            orgId={detailedview?.dealLevelSummary?.orgId}
                            dealName={
                                detailedview?.dealLevelSummary?.dealName ||
                                detailedview?.dealLevelSummary?.dealId
                            }
                            dealCategoryId={
                                detailedview?.dealLevelSummary?.dealCategoryId ||
                                location?.state?.dealCategoryId
                            }
                        />
                    )}
                </>
            )}
        </div>
    )
}

export default DealDetailView
